import React, { useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Section = ({ title, children }) => (
  <div className="mb-8">
    {title && (
      <h2 className="font-display text-white text-xl sm:text-2xl uppercase tracking-wide mt-8 mb-3">
        {title}
      </h2>
    )}
    <div className="text-gray-300 text-[15px] leading-relaxed space-y-4">{children}</div>
  </div>
);

const List = ({ items }) => (
  <ul className="list-disc pl-5 space-y-2">
    {items.map((t, i) => (
      <li key={i}>{t}</li>
    ))}
  </ul>
);

export default function TermsAndConditionsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black" data-testid="terms-conditions-page">
      <Header />
      <main className="pt-28 pb-20 px-6">
        <div className="container mx-auto max-w-3xl">
          <p className="text-gray-500 uppercase text-xs tracking-[0.3em] mb-3">The Dillon Blueprint</p>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl uppercase leading-none">
            Terms and Conditions
          </h1>
          <p className="text-gray-500 text-sm mt-4">
            Effective date: August 30, 2026 | thedillonblueprint.com/terms-and-conditions
          </p>
          <div className="h-px bg-neutral-800 my-8" />

          <Section>
            <p>
              These Terms and Conditions (“Terms”) govern your use of thedillonblueprint.com and your
              purchase of or participation in services offered by Alou Dillon d/b/a The Dillon Blueprint
              (“The Dillon Blueprint,” “we,” “us,” or “our”). By using the website, submitting an
              application, purchasing a product or service, or participating in a program, you agree to
              these Terms. If you participate on behalf of a minor, you agree to these Terms for yourself
              and the minor to the extent permitted by law.
            </p>
          </Section>

          <Section title="1. Eligibility and Parent or Guardian Approval">
            <p>
              You must be at least 18 years old to enter into a binding purchase or service agreement on
              your own.
            </p>
            <p>
              For any athlete under 18, a parent or legal guardian must complete a one-time enrollment
              approval - agreeing to these Terms, our Privacy Policy, and our liability waiver, and
              setting up payment before the athlete's first paid session. Once that enrollment approval
              is complete, the athlete may communicate, schedule, and participate directly with us
              without a parent present for each individual interaction. A parent or guardian must submit
              the initial application on behalf of a child under 13.
            </p>
            <p>
              Regardless of how day-to-day communication is handled, the parent or guardian remains
              responsible for the minor's compliance with these Terms and for providing complete,
              accurate health, emergency, and contact information, and remains the party who approved
              enrollment, payment, and the liability waiver.
            </p>
          </Section>

          <Section title="2. Services">
            <p>
              The Dillon Blueprint may offer basketball training, online or in-person fitness coaching,
              guided workout programming, film review and mentorship, mindset or accountability support,
              digital resources, community access, apparel, events, and related services. Program
              details, schedules, prices, equipment requirements, package expirations, and deliverables
              may be described on the website, booking page, checkout page, invoice, proposal, or
              separate service agreement.
            </p>
            <p>
              We may reasonably modify instructors, facilities, platforms, session formats, schedules, or
              program details when necessary, while attempting to preserve the core value of the
              purchased service.
            </p>
          </Section>

          <Section title="3. Applications and No Guarantee of Acceptance">
            <p>
              Submitting an application does not guarantee acceptance, availability, a roster spot, a
              particular coach, or a specific schedule. We may accept, decline, place on a waitlist, or
              recommend a different service based on fit, capacity, safety, facility access, or other
              legitimate business considerations.
            </p>
          </Section>

          <Section title="4. Health, Fitness, and Medical Disclaimer">
            <p>
              Basketball and fitness activities involve physical exertion and inherent risks, including
              falls, collisions, overexertion, sprains, strains, fractures, illness, serious injury,
              disability, or death. Participants should consult a qualified healthcare professional
              before beginning a new exercise program, especially if they have an injury, medical
              condition, pregnancy, recent surgery, or other health concern.
            </p>
            <p>
              The Dillon Blueprint does not provide medical diagnosis, treatment, physical therapy, or
              individualized medical advice. Nutrition guidance is general educational information unless
              expressly provided by a properly licensed professional acting within that professional
              scope. Stop exercising and seek appropriate medical attention if you experience chest
              pain, faintness, severe shortness of breath, sudden weakness, sharp pain, or other
              concerning symptoms.
            </p>
            <p>
              To the extent permitted by law, participants knowingly accept the inherent risks of
              physical activity. A separate waiver, release, emergency form, medical acknowledgment, or
              parent consent form may be required. These Terms do not replace those documents.
            </p>
          </Section>

          <Section title="5. Participant Responsibilities">
            <List
              items={[
                'Provide accurate contact, age, health, injury, limitation, emergency, and experience information and update it when circumstances change.',
                'Follow coaching instructions, facility rules, equipment guidance, and reasonable safety directions.',
                'Use appropriate footwear, clothing, hydration, space, and equipment, and inspect personal equipment before use.',
                'For online workouts, maintain a safe exercise area and ensure that any dumbbells, mini resistance bands, chair, bench, or other equipment are suitable, stable, and in good condition.',
                'Do not participate while impaired or when a medical professional has advised against participation.',
                'Treat coaches, staff, participants, facilities, and other users respectfully and do not engage in harassment, threats, unsafe conduct, or disruption.',
              ]}
            />
          </Section>

          <Section title="6. Scheduling, Attendance, Cancellations, and Program Changes">
            <p>
              Specific programs may include a cancellation window, attendance rule, package expiration,
              rescheduling limit, or no-show policy disclosed at booking, checkout, or enrollment. Those
              disclosed program-specific rules are incorporated into these Terms. A late cancellation or
              missed session may be counted as used when the applicable program terms say so.
            </p>
            <p>
              If no specific rule was disclosed, contact us as soon as possible when you need to cancel
              or reschedule. Requests for make-up sessions, credits, extensions, or exceptions are
              reviewed case by case and are not guaranteed. We may cancel or reschedule sessions because
              of facility access, coach availability, weather, safety, low enrollment, or events outside
              our reasonable control. When we cancel, we may provide a make-up session, credit,
              extension, alternative service, or refund, as reasonable under the circumstances.
            </p>
          </Section>

          <Section title="7. Prices, Payments, Packages, and Subscriptions">
            <p>
              Prices are shown in U.S. dollars unless stated otherwise and may change for future
              purchases. You agree to pay all charges, taxes, and fees disclosed at purchase. Payments
              may be processed by third-party providers, and their terms may also apply.
            </p>
            <p>
              Packages and prepaid services must be used within any expiration period disclosed at
              purchase. Sessions and packages are personal and may not be transferred or resold unless we
              approve the transfer in writing.
            </p>
            <p>
              If you purchase a recurring subscription, you authorize the disclosed recurring charges
              until cancellation. You must cancel using the method and within the time stated at
              checkout or in the program agreement to prevent the next charge. Canceling stops future
              renewals but does not automatically create a refund for a prior billing period or
              partially used period, except when required by law or expressly stated.
            </p>
          </Section>

          <Section title="8. Refunds, Credits, and Exchanges">
            <p>
              Unless a checkout page, product page, invoice, or separate agreement states a specific
              policy, requests for refunds, credits, extensions, exchanges, or exceptions are reviewed
              case by case. Approval is not guaranteed and may depend on services already delivered,
              costs already incurred, attendance, timing, safety issues, product condition, and
              applicable law. Completed sessions, delivered digital materials, completed film work, and
              used subscription periods are generally not refundable.
            </p>
            <p>
              Apparel returns or exchanges are subject to any store policy shown at purchase.
              Customized, worn, washed, damaged, or final-sale products may be ineligible for return
              except where the law requires otherwise.
            </p>
          </Section>

          <Section title="9. Film, Media, and User-Provided Content">
            <p>
              If you submit game film, training footage, statistics, photos, documents, music, or other
              content, you represent that you have the right and permission to provide it for the
              requested service. You grant The Dillon Blueprint a limited, nonexclusive license to
              access, copy, edit, analyze, and use that content only as reasonably necessary to deliver
              the service, maintain records, and exercise rights you separately authorize.
            </p>
            <p>
              We will not use an identifiable participant's image, voice, testimonial, or results
              publicly for promotional purposes without appropriate authorization. A parent or guardian
              must authorize promotional use involving a minor.
            </p>
            <p>Additional media-release terms may apply.</p>
          </Section>

          <Section title="10. Intellectual Property">
            <p>
              The website, brand names, logos, training methods, workout plans, educational materials,
              film breakdowns, videos, graphics, written content, and other materials created by or for
              The Dillon Blueprint are owned by The Dillon Blueprint or its licensors and are protected
              by applicable intellectual-property laws.
            </p>
            <p>
              Unless we give written permission, purchased or shared materials are for the client's
              personal, noncommercial use. You may not reproduce, sell, publish, distribute, upload,
              share login access, create competing materials from, or commercially exploit them.
            </p>
          </Section>

          <Section title="11. Results and Testimonials">
            <p>
              Athletic, fitness, body-composition, recruiting, playing-time, scholarship, business, and
              other results vary. Testimonials, statistics, transformations, and examples reflect
              individual experiences and do not guarantee that another participant will achieve the same
              or similar outcome. Results depend on factors including attendance, effort, prior
              experience, health, nutrition, sleep, competition, coaching environment, and circumstances
              outside our control.
            </p>
          </Section>

          <Section title="12. SMS Terms">
            <p>
              By selecting an optional SMS consent checkbox, you agree to receive non-marketing text
              messages from The Dillon Blueprint about your application, scheduling, training sessions,
              and training updates. Message frequency varies. Message and data rates may apply. Reply
              STOP to opt out or HELP for help. Consent to receive text messages is not required to
              submit an application or purchase a service. Marketing text messages, if offered, require
              separate consent where required by law.
            </p>
            <p>
              After you opt out, we may send a final confirmation message and may retain the opt-out
              record. Wireless carriers are not liable for delayed or undelivered messages. Delivery is
              not guaranteed and depends on your carrier and device. For assistance, reply HELP or email{' '}
              <a href="mailto:aloudillon@tdbtraining.com" className="text-white underline">
                aloudillon@tdbtraining.com
              </a>
              .
            </p>
          </Section>

          <Section title="13. Email and Other Communications">
            <p>
              We may send service-related emails or other communications necessary to administer an
              application, purchase, or program. Promotional emails may be unsubscribed from using the
              link in the message or by contacting us. You are responsible for keeping your contact
              information current.
            </p>
          </Section>

          <Section title="14. Third-Party Platforms, Facilities, and Links">
            <p>
              Services may depend on third-party payment processors, scheduling systems, communications
              providers, community platforms, ecommerce tools, video services, schools, gyms, or other
              facilities. Their separate terms and policies may apply. We are not responsible for
              third-party outages, policies, acts, content, or services outside our reasonable control.
            </p>
          </Section>

          <Section title="15. Suspension or Termination">
            <p>
              We may suspend or end access to a program, website feature, community, or service for
              nonpayment, unsafe conduct, harassment, misuse of materials, repeated disruption, rule
              violations, fraud, unlawful activity, or a material breach of these Terms. When
              appropriate, we will consider the circumstances and any available credit or refund case by
              case, subject to applicable law and costs already incurred.
            </p>
          </Section>

          <Section title="16. Disclaimer of Warranties">
            <p>
              To the fullest extent permitted by law, the website and services are provided on an "as
              available" basis. We do not promise uninterrupted availability, error-free digital
              services, acceptance into a program, facility availability, or any particular athletic,
              fitness, health, recruiting, financial, or performance result. Nothing in these Terms
              excludes warranties or rights that cannot lawfully be excluded.
            </p>
          </Section>

          <Section title="17. Limitation of Liability">
            <p>
              To the fullest extent permitted by law, The Dillon Blueprint and its owner, coaches,
              contractors, and representatives will not be liable for indirect, incidental, special,
              consequential, exemplary, or punitive damages, or for lost opportunities, profits, data,
              playing time, scholarships, or similar losses arising from the website or services.
            </p>
            <p>
              To the fullest extent permitted by law, our total liability for a claim will not exceed
              the amount you paid to The Dillon Blueprint for the specific product or service giving
              rise to that claim during the six months before the event. This limitation does not apply
              to liability that cannot legally be limited, including liability for gross negligence,
              reckless or intentional misconduct, or other nonwaivable rights under applicable law.
            </p>
          </Section>

          <Section title="18. Indemnification">
            <p>
              To the extent permitted by law, you agree to reimburse and protect The Dillon Blueprint
              from third-party claims, losses, or reasonable costs arising from your unlawful conduct,
              misuse of the website or materials, violation of these Terms, or submission of content you
              did not have the right to provide. This section does not require a participant to
              indemnify The Dillon Blueprint for our own nonwaivable misconduct.
            </p>
          </Section>

          <Section title="19. Governing Law and Disputes">
            <p>
              These Terms are governed by the laws of the State of Wisconsin, without regard to
              conflict-of-law rules. Before filing a lawsuit, the parties agree to make a good-faith
              effort to resolve the dispute informally by written notice and discussion for at least 30
              days. Unless applicable law requires otherwise, legal proceedings must be brought in a
              state or federal court with jurisdiction in Milwaukee County, Wisconsin.
            </p>
          </Section>

          <Section title="20. Changes to Services or These Terms">
            <p>
              We may update these Terms as the business, services, technology, or law changes. The
              revised version will be posted with a new effective date. Changes apply prospectively
              unless applicable law permits otherwise. Your continued use after the effective date means
              you accept the updated Terms.
            </p>
          </Section>

          <Section title="21. General Terms">
            <p>
              If a provision of these Terms is found unenforceable, the remaining provisions remain in
              effect. A failure to enforce a provision is not a waiver. You may not transfer your rights
              or obligations without our written consent. We may assign these Terms as part of a
              business transfer or reorganization.
            </p>
            <p>
              These Terms, together with the Privacy Policy and any applicable checkout terms, program
              rules, waiver, media release, invoice, or separate service agreement, form the agreement
              between the parties for the relevant service. If a signed service agreement or waiver
              directly conflicts with these website Terms, the more specific signed document controls
              for that issue.
            </p>
          </Section>

          <Section title="22. Contact">
            <p>Questions about these Terms may be sent to:</p>
            <p className="not-italic">
              Alou Dillon d/b/a The Dillon Blueprint
              <br />
              Milwaukee, Wisconsin
              <br />
              Email:{' '}
              <a href="mailto:aloudillon@tdbtraining.com" className="text-white underline">
                aloudillon@tdbtraining.com
              </a>
            </p>
          </Section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
